// Extend Window to include Canny SDK
declare global {
  interface Window {
    Canny?: CannyFunction;
  }
}

type CannyFunction = {
  (action: 'identify', options: CannyIdentifyOptions): void;
  q?: unknown[];
};

interface CannyIdentifyOptions {
  appID: string;
  user: {
    id: string;
    email: string;
    name: string;
    avatarURL?: string;
    created?: string;
  };
}

let sdkLoaded = false;

/**
 * Load the Canny SDK script into the document head.
 * Idempotent — safe to call multiple times.
 */
function loadCannySDK(): void {
  if (sdkLoaded || document.getElementById("canny-jssdk")) return;
  sdkLoaded = true;

  // Initialize the Canny queue if not present
  if (typeof window.Canny !== "function") {
    const c = function (...args: unknown[]) {
      c.q!.push(args);
    } as CannyFunction;
    c.q = [];
    window.Canny = c;
  }

  const script = document.createElement("script");
  script.id = "canny-jssdk";
  script.type = "text/javascript";
  script.async = true;
  script.src = "https://sdk.canny.io/sdk.js";
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript.parentNode?.insertBefore(script, firstScript);
}

/**
 * Identify the current user to Canny so they're recognized on the board.
 * Call once when the user is authenticated and profile data is available.
 */
export function identifyCannyUser(user: {
  id: string;
  email: string;
  fullName: string;
  avatarURL?: string | null;
  createdAt?: string;
}): void {
  const appID = import.meta.env.VITE_CANNY_APP_ID;
  if (!appID) return;

  loadCannySDK();

  window.Canny?.("identify", {
    appID,
    user: {
      id: user.id,
      email: user.email,
      name: user.fullName,
      ...(user.avatarURL && { avatarURL: user.avatarURL }),
      ...(user.createdAt && { created: user.createdAt }),
    },
  });
}

/**
 * Open the Canny feature request board in a new tab.
 */
export function openCannyBoard(): void {
  const url = import.meta.env.VITE_CANNY_BOARD_URL || "https://dnceclub.canny.io";
  window.open(url, "_blank", "noopener,noreferrer");
}
